import { collection, collectionGroup, deleteDoc, doc, getDoc, setDoc, Timestamp, updateDoc, } from "firebase/firestore";
import { warnUnknownFields } from "./converterWarnings";
import { prepareForFirestore } from "./utils";
const SERVICE_FIELDS = [
    "id",
    "path",
    "companyId",
    "name",
    "description",
    "website",
    "serviceBrief",
    "contactDetails",
    "faq",
    "notifications",
    "knowledgeBase",
    "additionalResources",
    "isActive",
    "tags",
    "onboarding",
    "createdAt",
    "updatedAt",
];
const DEFAULT_NOTIFICATION_FROM_NAME = "{{ service.name }} - Agent";
const DEFAULT_NOTIFICATION_SUBJECT = "New call from {{ callerNumber }} about {{ callSubject }}";
const DEFAULT_NOTIFICATION_BODY = "A new call from {{ callerNumber }} about {{ callSubject }} was just handled.\n\n{{ linkToCall }}";
function defaultServiceNotifications() {
    return {
        enabled: true,
        emails: [],
        fromName: DEFAULT_NOTIFICATION_FROM_NAME,
        subject: DEFAULT_NOTIFICATION_SUBJECT,
        body: DEFAULT_NOTIFICATION_BODY,
    };
}
function normalizeServiceNotifications(value) {
    const defaults = defaultServiceNotifications();
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return defaults;
    }
    const data = value;
    return {
        enabled: typeof data.enabled === "boolean" ? data.enabled : defaults.enabled,
        emails: Array.isArray(data.emails)
            ? data.emails.filter((email) => typeof email === "string")
            : defaults.emails,
        fromName: typeof data.fromName === "string" && data.fromName.trim()
            ? data.fromName
            : defaults.fromName,
        subject: typeof data.subject === "string" && data.subject.trim()
            ? data.subject
            : defaults.subject,
        body: typeof data.body === "string" && data.body.trim()
            ? data.body
            : defaults.body,
    };
}
export const serviceConverter = {
    toFirestore(value) {
        return prepareForFirestore(value);
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        warnUnknownFields("packages/ui Service.fromFirestore", data, SERVICE_FIELDS);
        const name = data.name || "";
        return {
            id: snapshot.id,
            path: snapshot.ref.path,
            companyId: snapshot.ref.parent.parent?.id || data.companyId || "",
            name,
            description: data.description || "",
            website: data.website || "",
            serviceBrief: data.serviceBrief || "",
            contactDetails: data.contactDetails || "",
            faq: data.faq || "",
            notifications: normalizeServiceNotifications(data.notifications),
            knowledgeBase: data.knowledgeBase || "",
            additionalResources: data.additionalResources || [],
            isActive: data.isActive ?? true,
            tags: [],
            createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate().toISOString()
                : data.createdAt || new Date().toISOString(),
            updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate().toISOString()
                : data.updatedAt || null,
        };
    },
};
export function servicesCollection(db, companyId) {
    return collection(db, "companies", companyId, "services").withConverter(serviceConverter);
}
export function allServicesQuery(db) {
    return collectionGroup(db, "services").withConverter(serviceConverter);
}
export function serviceDocument(db, companyId, serviceId) {
    return doc(db, "companies", companyId, "services", serviceId).withConverter(serviceConverter);
}
export async function fetchService(db, companyId, serviceId) {
    const snapshot = await getDoc(serviceDocument(db, companyId, serviceId));
    return snapshot.exists() ? snapshot.data() : null;
}
export async function createService(db, data) {
    if (!data.companyId) {
        throw new Error("Company ID is required to create a service");
    }
    const docRef = doc(servicesCollection(db, data.companyId));
    // Tags are stored in a subcollection, not embedded in the service document.
    // Strip them from the service payload if present on the caller state.
    const { tags: _tags, ...serviceData } = data;
    await setDoc(docRef, {
        ...serviceData,
        id: docRef.id,
        createdAt: new Date(),
    });
    return docRef.id;
}
export async function updateService(db, companyId, serviceId, data) {
    const docRef = serviceDocument(db, companyId, serviceId);
    const { tags: _tags, ...serviceData } = data;
    await updateDoc(docRef, serviceData);
}
export async function deleteService(db, companyId, serviceId) {
    await deleteDoc(serviceDocument(db, companyId, serviceId));
}
